import {
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { PRODUCT_USAGE_TYPE } from "../../constants/Variables";
import globalStyle from "../../globalStyle";
import { handleOpenSnackBar } from "../../utils";
import { translateText } from "../../utils/translationService";
import CustomDropDown from "../CutomDropDown";
import Label from "../Label";
import TextInput from "../TextInputs";

const BasicInformation = ({
  inputValues,
  setInputValues,
  allCategories,
  allSubSubCategories,
  allSubCategories,
  allBrands,
  inputError,
  setInputError,
  fetchchedAllSubCategories,
  fetchchedAllSubSubCategories,
}) => {
  const { t } = useTranslation(["products", "common"]);
  const [isLoading, setIsLoading] = useState({
    title: false,
  });

  // Get the current currentLanguage from the redux store
  const { currentLanguage } = useSelector((state) => state.languageReducer);

  const handleChangeValues = (event) => {
    const { name, value } = event.target;

    if (name === "name_ro") {
      if (value.length > 250) {
        setInputError((prev) => ({
          ...prev,
          name_ro: "validation.productTitleIsTooLongInRomanian",
        }));
        return;
      }
    }
    if (name === "name_en") {
      if (value.length > 250) {
        setInputError((prev) => ({
          ...prev,
          name_en: "validation.productTitleIsTooLongInEnglish",
        }));
        return;
      }
    }

    if (name === "category" && value) {
      fetchchedAllSubCategories(value);
      setInputValues((prev) => ({ ...prev, subCategory: "" }));
    }

    if (name === "subCategory" && value && inputValues.category) {
      fetchchedAllSubSubCategories(inputValues.category, value);
      setInputValues((prev) => ({ ...prev, subSubCategory: "" }));
    }

    if (name === "quantity") {
      if (Number(value) < 0) return;
    }

    if (name === "solutionType") {
      setInputValues((prev) => ({ ...prev, tags: [] }));
    }

    setInputError((prev) => ({
      ...prev,
      [name]: "",
    }));
    setInputValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleTranslateToEnglish = async () => {
    if (!inputValues.name_ro) {
      handleOpenSnackBar(
        "error",
        t("validation.productTitleIsRequiredInRomanian"),
      );
      return;
    }
    setIsLoading((prev) => ({ ...prev, title: true }));
    try {
      const [titleTranslation] = await Promise.all([
        translateText(inputValues.name_ro),
      ]);
      setInputValues((prev) => ({ ...prev, name_en: titleTranslation }));
      setInputError((prev) => ({ ...prev, name_en: "" }));
    } catch (error) {
      handleOpenSnackBar("error", t("error.convertingToEnglishError"));
    } finally {
      setIsLoading((prev) => ({ ...prev, title: false }));
    }
  };

  return (
    <>
      <Grid item md={12} mt={2}>
        <Typography fontWeight={600} fontSize={20}>
          {t("basicInformation")}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Label
          required
          labelText={`${t("productTitleRo")}`}
          htmlFor={`product-name-ro`}
        />
        <TextInput
          id={`product-name-ro`}
          placeholder={`${t("enterTitleRo")} `}
          required
          type="text"
          name={`name_ro`}
          value={inputValues.name_ro}
          onChange={handleChangeValues}
          inputError={Boolean(inputError.name_ro)}
          helperText={t(inputError.name_ro)}
          InputEndIcon={
            <Typography variant="body2" color="text.secondary">
              {inputValues.name_ro.length}/250
            </Typography>
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          justifyContent="space-between"
          mb={"5px"}
        >
          <Label
            required
            labelText={`${t("productTitleEn")}`}
            htmlFor={`product-name-en`}
            labelProps={{
              marginBottom: 0,
            }}
          />
          <Chip
            size="small"
            label={t("translateToEnglish", { ns: "common" })}
            onClick={handleTranslateToEnglish}
            disabled={isLoading.title}
            icon={
              isLoading.title ? (
                <CircularProgress size={16} color="primary" />
              ) : null
            }
          />
        </Stack>
        <TextInput
          id={`product-name-en`}
          placeholder={`${t("enterTitleEn")} `}
          required
          type="text"
          name={`name_en`}
          value={inputValues.name_en}
          onChange={handleChangeValues}
          inputError={Boolean(inputError.name_en)}
          helperText={t(inputError.name_en)}
          InputEndIcon={
            <Typography variant="body2" color="text.secondary">
              {inputValues.name_en.length}/250
            </Typography>
          }
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <Label
          labelText={t("selectCategory")}
          required
          htmlFor={"product-category"}
        />
        <CustomDropDown
          id={"product-category"}
          name={"category"}
          value={inputValues.category}
          onChange={handleChangeValues}
          inputError={Boolean(inputError.category)}
          errorText={t(inputError.category)}
          sx={{
            "& .MuiSelect-outlined": {
              textTransform: "capitalize",
            },
          }}
        >
          <MenuItem value="" disabled>
            {t("selectCategory")}
          </MenuItem>
          {allCategories?.map((item, index) => (
            <MenuItem
              key={index}
              value={item?._id}
              sx={{ textTransform: "capitalize" }}
            >
              {item.name[currentLanguage]}
            </MenuItem>
          ))}
        </CustomDropDown>
      </Grid>
      <Grid item xs={12} md={4}>
        <Label
          labelText={t("selectSubCategory")}
          required
          htmlFor={"product-sub-category"}
        />
        <CustomDropDown
          id={"product-sub-category"}
          name={"subCategory"}
          value={inputValues.subCategory}
          onChange={handleChangeValues}
          inputError={Boolean(inputError.subCategory)}
          errorText={t(inputError.subCategory)}
          sx={{
            "& .MuiSelect-outlined": {
              textTransform: "capitalize",
            },
          }}
          disabled={!inputValues.category}
        >
          <MenuItem value="" disabled>
            {t("selectSubCategory")}
          </MenuItem>
          {allSubCategories?.map((item, index) => (
            <MenuItem
              key={index}
              value={item?._id}
              sx={{ textTransform: "capitalize" }}
            >
              {item.name[currentLanguage]}
            </MenuItem>
          ))}
        </CustomDropDown>
      </Grid>
      <Grid item xs={12} md={4}>
        <Label
          labelText={t("selectSubSubCategory")}
          required
          htmlFor={"product-sub-sub-category"}
        />
        <CustomDropDown
          id={"product-sub-sub-category"}
          name={"subSubCategory"}
          value={inputValues.subSubCategory}
          onChange={handleChangeValues}
          inputError={Boolean(inputError.subSubCategory)}
          errorText={t(inputError.subSubCategory)}
          sx={{
            "& .MuiSelect-outlined": {
              textTransform: "capitalize",
            },
          }}
          disabled={!inputValues.subCategory}
        >
          <MenuItem value="" disabled>
            {t("selectSubSubCategory")}
          </MenuItem>
          {allSubSubCategories?.map((item, index) => (
            <MenuItem
              key={index}
              value={item?._id}
              sx={{ textTransform: "capitalize" }}
            >
              {item.name[currentLanguage]}
            </MenuItem>
          ))}
        </CustomDropDown>
      </Grid>
      <Grid item xs={12} md={4}>
        <Label
          labelText={t("selectBrand")}
          htmlFor={"product-brand"}
          optional
        />
        {/* <TextInput
          id={"product-brand"}
          styleProps={globalStyle.textField}
          placeholder={t("enterBrandName")}
          name={"brand"}
          value={inputValues.brand}
          onChange={handleChangeValues}
        /> */}
        <CustomDropDown
          id={"product-brand"}
          name={"brand"}
          value={inputValues.brand}
          onChange={handleChangeValues}
          inputError={Boolean(inputError.brand)}
          errorText={t(inputError.brand)}
          sx={{
            "& .MuiSelect-outlined": {
              textTransform: "capitalize",
            },
          }}
        >
          <MenuItem value="">{t("noBrand")}</MenuItem>
          {allBrands?.map((item, index) => (
            <MenuItem
              key={index}
              value={item?._id}
              sx={{ textTransform: "capitalize" }}
            >
              {item.title[currentLanguage]}
            </MenuItem>
          ))}
        </CustomDropDown>
      </Grid>

      <Grid item xs={12} md={4}>
        <Label
          labelText={t("solutionType")}
          required
          htmlFor={`product-solution-type`}
        />

        <CustomDropDown
          name="solutionType"
          value={inputValues.solutionType}
          onChange={handleChangeValues}
          inputError={Boolean(inputError.solutionType)}
          errorText={t(inputError.solutionType)}
          multiple
        >
          <MenuItem value="" disabled>
            {t("selectSolutionType")}
          </MenuItem>
          {PRODUCT_USAGE_TYPE.map((item, index) => (
            <MenuItem key={index} value={item.value}>
              {item.label[currentLanguage]}
            </MenuItem>
          ))}
        </CustomDropDown>
      </Grid>
    </>
  );
};

export default BasicInformation;
